import { supabase } from '../lib/supabase';
import { toCamelCase, toSnakeCase } from '../utils/formatters';

    export const sellerProductService = {
      async getSellerProductsBySellerId(sellerId) {
        try {
          const { data, error } = await supabase
            .from('seller_products')
            .select(`
              id,
              views_count,
              seller_id,
              product_id,
              stock_code,
              seller_status:status,
              vin_sn,
              effective_date,
              seller_price:price,
              seller_stock_amount:stock_amount,
              qty_sold,
              mycompcount,
              seller_description:description,
              seller_image_url,
              condition,
              isdeleted,
              is_certified,
              staffpick,
              is_self_manufacturing,
              manufacturer_id,
              sell_product_only,
              keywords,
              seasonal_setting,
              holiday_setting,
              products (
                name,
                description,
                price,
                stock_quantity,
                sold_count,
                upc,
                sku,
                thumbnail_path,
                gltf_file_path,
                is_featured,
                is_certified,
                staffpick,
                component_count,
                average_rating,
                total_ratings,
                added_count,
                is_3d_manufacturing,
                is_cnc_capable,
                categories ( name ),
                manufacturers ( name )
              )
            `)
            .eq('seller_id', sellerId)
            .or('isdeleted.is.null,isdeleted.eq.false')
            .order('display_order', { ascending: true })
            .order('created_at', { ascending: false });
    
          if (error) {
            console.error('Error fetching seller products with joins:', error);
            throw new Error(error.message);
          }

          const camelCasedData = data.map(sp => {
            return {
              id: sp.id,
              sellerId: sp.seller_id,
              productId: sp.product_id,
              stockCode: sp.stock_code,
              status: sp.seller_status,
              vinSn: sp.vin_sn,
              effectiveDate: sp.effective_date,
              price: sp.seller_price,
              stockAmount: sp.seller_stock_amount,
              qtySold: sp.qty_sold,
              viewsCount: Number(sp.views_count) || 0,
              myCompCount: sp.products?.component_count || 0,
              description: sp.seller_description,
              sellerImageUrls: sp.seller_image_url,
              vinSn: sp.vin_sn,
              condition: sp.condition,
              sellerIsCertified: sp.is_certified,
              sellerStaffpick: sp.staffpick,
              isSelfManufacturing: sp.is_self_manufacturing !== false,
              manufacturerId: sp.manufacturer_id,
              sellProductOnly: Boolean(sp.sell_product_only), // NEW: Sell product assembly only flag
              keywords: sp.keywords,
              seasonalSetting: sp.seasonal_setting,
              holidaySetting: sp.holiday_setting,

              // Flatten nested product data with prefix
              productName: sp.products?.name,
              productDescription: sp.products?.description,
              productPrice: sp.products?.price,
              productStockQuantity: sp.products?.stock_quantity,
              productIs3dManufacturing: sp.products?.is_3d_manufacturing,
              productIsCncCapable: sp.products?.is_cnc_capable,
              productSoldCount: sp.products?.sold_count,
              productUPC: sp.products?.upc,
              productSKU: sp.products?.sku,
              productThumbnailPath: sp.products?.thumbnail_path,
              productGltfFilePath: sp.products?.gltf_file_path,
              productIsFeatured: sp.products?.is_featured,
              productIsCertified: sp.products?.is_certified,
              productStaffpick: sp.products?.staffpick,
              productComponentCount: sp.products?.component_count,
              averageRating: sp.products?.average_rating,
              totalRatings: sp.products?.total_ratings,
              addedCount: sp.products?.added_count,
    
              categoryName: sp.products?.categories?.name,
              manufacturerName: sp.products?.manufacturers?.name,
            };
          });
          
          return camelCasedData;
    
        } catch (error) {
          console.error('Failed to fetch seller products with joins:', error);
          throw error;
        }
      },

      async getPublicSellerProductsBySellerId(sellerId) {
        try {
          // This RPC should be configured in Supabase to allow anonymous access
          // and return public-facing seller product data.
          const { data, error } = await supabase.rpc('get_public_seller_products_by_seller_id', {
            p_seller_id: sellerId
          });

          if (error) {
            console.error('Error fetching public seller products via RPC:', error);
            throw new Error(error.message);
          }

          // The RPC should return data structured similarly to the joined query
          // so toCamelCase can process it.
          const camelCasedData = data.map(toCamelCase);
          return camelCasedData;

        } catch (error) {
          console.error('Failed to fetch public seller products:', error);
          throw error;
        }
      },


  // Add any other CRUD operations for seller_products here if needed
  async createSellerProduct(sellerProductData) {
    const snakeCaseData = toSnakeCase(sellerProductData);
    snakeCaseData.isdeleted = false;
    const { data, error } = await supabase
      .from('seller_products')
      .insert([snakeCaseData])
      .select();

    if (error) {
      console.error('Error creating seller product:', error);
      throw error;
    }
    return data[0]; // Return the created record
  },
  async update(id, updates) {
    const snakeCaseUpdates = toSnakeCase(updates);
    const { data, error } = await supabase
      .from('seller_products')
      .update(snakeCaseUpdates)
      .eq('id', id)
      .select();

    if (error) {
      console.error('Error updating seller product:', error);
      throw error;
    }

    if (!data || data.length === 0) {
      throw new Error("Save failed. This listing could not be updated (Row Level Security restriction or record not found).");
    }

    return data;
  },
  async delete(id) {
    // 1. Mark the seller_product as deleted
    const { data: sp, error } = await supabase
      .from('seller_products')
      .update({ isdeleted: true })
      .eq('id', id)
      .select('seller_id, product_id')
      .single();
    if (error) throw error;

    // 2. Mark all associated seller_components for this product as deleted
    if (sp?.seller_id && sp?.product_id) {
      // Find components linked to this product
      const { data: crossRefData } = await supabase
        .from('product_components')
        .select('component_id')
        .eq('product_id', sp.product_id);
      
      const componentIds = crossRefData?.map(cr => cr.component_id) || [];
      
      if (componentIds.length > 0) {
        await supabase
          .from('seller_components')
          .update({ isdeleted: true })
          .eq('seller_id', sp.seller_id)
          .in('component_id', componentIds);
      }
    }
  },
};
